import React, { useRef } from "react";
import { TAG_ICONS } from "./tagIcons";

/* Wavy-edged rectangle (outward scallop bumps on all four sides) */
const wavyRectPath = (x0, y0, x1, y1, bump = 12) => {
  const hCount = Math.max(3, Math.round((x1 - x0) / bump));
  const hSeg = (x1 - x0) / hCount;
  const vCount = Math.max(3, Math.round((y1 - y0) / bump));
  const vSeg = (y1 - y0) / vCount;
  const r = 0.62;
  let d = `M${x0},${y0} `;
  for (let i = 0; i < hCount; i++) d += `A${(hSeg * r).toFixed(2)},${(hSeg * r).toFixed(2)} 0 0 1 ${(x0 + (i + 1) * hSeg).toFixed(2)},${y0} `;
  for (let i = 0; i < vCount; i++) d += `A${(vSeg * r).toFixed(2)},${(vSeg * r).toFixed(2)} 0 0 1 ${x1},${(y0 + (i + 1) * vSeg).toFixed(2)} `;
  for (let i = 0; i < hCount; i++) d += `A${(hSeg * r).toFixed(2)},${(hSeg * r).toFixed(2)} 0 0 1 ${(x1 - (i + 1) * hSeg).toFixed(2)},${y1} `;
  for (let i = 0; i < vCount; i++) d += `A${(vSeg * r).toFixed(2)},${(vSeg * r).toFixed(2)} 0 0 1 ${x0},${(y1 - (i + 1) * vSeg).toFixed(2)} `;
  return d + "Z";
};

/* Gentle in-out waves along a vertical edge */
const waveSide = (x, y0, y1, n, amp) => {
  const seg = (y1 - y0) / n;
  let d = "";
  for (let i = 0; i < n; i++) {
    const cx = x + (i % 2 === 0 ? amp : -amp);
    d += `Q${cx.toFixed(1)},${(y0 + (i + 0.5) * seg).toFixed(1)} ${x},${(y0 + (i + 1) * seg).toFixed(1)} `;
  }
  return d;
};

/* Open C-ring hanger with rounded tips (single subpath — matches supplied stem tag outline) */
const HANGER_RING =
  "M39.35,23.46 A13,13 0 1 1 47.74,28.80 A2.75,2.75 0 0 1 48.70,23.39 A7.5,7.5 0 1 0 43.86,20.30 A2.75,2.75 0 0 1 39.35,23.46 Z";

/* Rounded-top plant stake body with pointed tip at y=146 */
const plantStakeBody = (top, shoulder) =>
  `M42,${top + 8} A8,8 0 0 1 58,${top + 8} L58,${shoulder} L50,146 L42,${shoulder} Z`;

export const STAKE_SIZES = ["15cm", "20cm", "25cm"];

export const TAG_SHAPES = {
  heritage_panel_stake: {
    label: "HERITAGE PANEL STAKE",
    head: wavyRectPath(10, 10, 90, 60, 11),
    stem: "M39,60 L61,60 L52,142 Q50,147 48,142 Z",
    textY: 36,
    textW: 64,
    engrave: { x: 16, y: 16, w: 68, h: 38 },
  },
  heritage_stem_tag: {
    label: "HERITAGE STEM TAG",
    head: HANGER_RING,
    stem: `M46,29.5 Q45.5,37 40,43 Q36,45.5 36,51 ${waveSide(36, 51, 131, 5, -3.2)}Q36,141 43,142 Q47,140.5 50,141.5 Q53,140.5 57,142 Q64,141 64,131 ${waveSide(64, 131, 51, 5, 3.2)}Q64,45.5 60,43 Q54.5,37 54,29.5 Q50,31.5 46,29.5 Z`,
    vertical: true,
    textCY: 95,
    textLen: 78,
    engrave: { x: 40, y: 51, w: 20, h: 84 },
  },
  etchery_panel_stake: {
    label: "ETCHERY PANEL STAKE",
    head: "M18,10 L82,10 Q86,10 86,14 L86,60 Q86,64 82,64 L18,64 Q14,64 14,60 L14,14 Q14,10 18,10 Z",
    stem: "M39,64 L61,64 L52,142 Q50,147 48,142 Z",
    textY: 38,
    textW: 66,
    engrave: { x: 18, y: 14, w: 64, h: 46 },
  },
  etchery_stem_tag: {
    label: "ETCHERY STEM TAG",
    head: HANGER_RING,
    stem: "M46,29.5 Q45.5,37 40,42 Q35,44.5 35,50 L35,140 Q35,146 41,146 L59,146 Q65,146 65,140 L65,50 Q65,44.5 60,42 Q54.5,37 54,29.5 Q50,31.5 46,29.5 Z",
    vertical: true,
    textCY: 96,
    textLen: 90,
    engrave: { x: 38, y: 48, w: 24, h: 94 },
  },
  etchery_plant_stake: {
    label: "ETCHERY PLANT STAKE",
    stem: "",
    vertical: true,
    defaultSize: "20cm",
    sizes: {
      "25cm": { head: plantStakeBody(4, 122), textCY: 65, textLen: 96, engrave: { x: 44, y: 16, w: 12, h: 100 } },
      "20cm": { head: plantStakeBody(30, 126), textCY: 81, textLen: 76, engrave: { x: 44, y: 42, w: 12, h: 78 } },
      "15cm": { head: plantStakeBody(56, 130), textCY: 96, textLen: 56, engrave: { x: 44, y: 68, w: 12, h: 58 } },
    },
  },
};

const resolveShape = (shape, size) => {
  if (!shape.sizes) return shape;
  const variant = shape.sizes[size] || shape.sizes[shape.defaultSize];
  return { ...shape, ...variant };
};

export const getEngraveArea = (shapeKey, size = "") =>
  resolveShape(TAG_SHAPES[shapeKey] || TAG_SHAPES.etchery_panel_stake, size).engrave;

export const isDarkHex = (hex) => {
  if (!hex) return false;
  const h = hex.replace("#", "");
  const r = parseInt(h.substring(0, 2), 16);
  const g = parseInt(h.substring(2, 4), 16);
  const b = parseInt(h.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.55;
};

const fit = (text, maxWidth, base) => Math.min(base, (maxWidth / Math.max(1, text.length)) / 0.62);

const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, v));

export const TagPreview = ({
  shapeKey, hex = "#EC2675", line1 = "", line2 = "", size = "",
  icon = null, onIconChange = null, showSafeArea = false,
  className = "", testid,
}) => {
  const shape = resolveShape(TAG_SHAPES[shapeKey] || TAG_SHAPES.etchery_panel_stake, size);
  const uid = React.useId().replace(/[^a-zA-Z0-9]/g, "");
  const svgRef = useRef(null);
  const dragging = useRef(false);
  const area = shape.engrave;
  const textColor = isDarkHex(hex) ? "#FDFBF7" : "#4A1B4D";
  const hasTwo = Boolean(line2);
  const serif = { fontFamily: "'Cormorant Garamond', serif", fontWeight: 600, letterSpacing: "0.08em" };
  const iconDef = icon && TAG_ICONS[icon.key];

  const handleMove = (e) => {
    if (!dragging.current || !svgRef.current || !onIconChange || !area) return;
    const r = svgRef.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width) * 100;
    const y = ((e.clientY - r.top) / r.height) * 150;
    onIconChange({
      ...icon,
      x: +clamp(x, area.x + 2, area.x + area.w - 2).toFixed(1),
      y: +clamp(y, area.y + 2, area.y + area.h - 2).toFixed(1),
    });
  };

  return (
    <svg
      ref={svgRef}
      viewBox="0 0 100 150"
      className={className}
      data-testid={testid}
      aria-hidden
      onPointerMove={onIconChange ? handleMove : undefined}
      onPointerUp={onIconChange ? () => { dragging.current = false; } : undefined}
    >
      <path
        d={`${shape.head || ""} ${shape.stem || ""}`}
        fill={hex}
        stroke="#4A1B4D"
        strokeOpacity="0.18"
        strokeWidth="1"
      />
      {showSafeArea && area && (
        <rect x={area.x} y={area.y} width={area.w} height={area.h} rx="2"
          fill="none" stroke={textColor} strokeOpacity="0.3" strokeDasharray="3 3" strokeWidth="0.7" />
      )}
      {iconDef && area && (
        <>
          <clipPath id={`clip${uid}`}>
            <rect x={area.x} y={area.y} width={area.w} height={area.h} rx="2" />
          </clipPath>
          <g clipPath={`url(#clip${uid})`}>
            <g transform={`translate(${icon.x} ${icon.y}) rotate(${icon.rotation || 0}) scale(${(icon.size || 16) / 24}) translate(-12 -12)`}>
              <path d={iconDef.d} fill={textColor} />
            </g>
          </g>
          {onIconChange && (
            <circle
              cx={icon.x} cy={icon.y} r={(icon.size || 16) / 2 + 3}
              fill="transparent" stroke={textColor} strokeOpacity="0.4" strokeDasharray="2 2" strokeWidth="0.7"
              style={{ cursor: "grab", touchAction: "none" }}
              data-testid="tag-icon-handle"
              onPointerDown={(e) => {
                dragging.current = true;
                svgRef.current?.setPointerCapture?.(e.pointerId);
              }}
            />
          )}
        </>
      )}
      {shape.vertical ? (
        <g transform={`rotate(-90 50 ${shape.textCY})`}>
          {line1 && (
            <text x="50" y={hasTwo ? shape.textCY - 3 : shape.textCY + 3.5}
              textAnchor="middle" fill={textColor}
              style={{ ...serif, fontSize: fit(line1, shape.textLen, 11) }}>
              {line1.toUpperCase()}
            </text>
          )}
          {line2 && (
            <text x="50" y={shape.textCY + 8} textAnchor="middle" fill={textColor}
              style={{ ...serif, fontStyle: "italic", fontWeight: 500, fontSize: fit(line2, shape.textLen, 8) }}>
              {line2}
            </text>
          )}
        </g>
      ) : (
        <>
          {line1 && (
            <text x="50" y={hasTwo ? shape.textY - 5 : shape.textY + 4}
              textAnchor="middle" fill={textColor}
              style={{ ...serif, fontSize: fit(line1, shape.textW || 56, 13) }}>
              {line1.toUpperCase()}
            </text>
          )}
          {line2 && (
            <text x="50" y={shape.textY + 10} textAnchor="middle" fill={textColor}
              style={{ ...serif, fontStyle: "italic", fontWeight: 500, fontSize: fit(line2, shape.textW || 56, 9) }}>
              {line2}
            </text>
          )}
        </>
      )}
    </svg>
  );
};
